import {useEffect, useRef, useState} from 'react';
import {AnimatePresence, motion, useReducedMotion} from 'motion/react';
import {VendorsPanel} from '@/components/vendors/VendorsPanel';
import {PublicPanel} from '@/components/public/PublicPanel';

export type Audience = 'vendors' | 'publico';

const TABS: [Audience, string][] = [
  ['vendors', 'Para comercio participante'],
  ['publico', 'Para visitantes'],
];

/**
 * Las dos audiencias de la landing (propuesta §04). El tab activo vive en el
 * hash (#vendors / #publico) para deep-linking desde campañas.
 */
export function AudienceTabs({value, onChange}: {value: Audience; onChange: (a: Audience) => void}) {
  const reduced = useReducedMotion();
  const [direction, setDirection] = useState(1);
  const listRef = useRef<HTMLDivElement>(null);

  // El hash sigue al tab, pero solo cuando el tab cambia. Escribirlo al montar
  // le inventaría un "#vendors" a quien entra al home limpio — y App lee el
  // hash en su propio efecto (que corre después que el de este hijo), así que
  // ese hash inventado terminaba disparando un scroll automático.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    history.replaceState(null, '', `#${value}`);
  }, [value]);

  function select(v: Audience) {
    if (v === value) return;
    setDirection(v === 'publico' ? 1 : -1);
    onChange(v);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const next = value === 'vendors' ? 'publico' : 'vendors';
    select(next);
    listRef.current?.querySelector<HTMLButtonElement>(`[data-tab="${next}"]`)?.focus();
  }

  return (
    <div>
      <div className="sticky top-16 z-40 bg-pine-900/85 py-3 backdrop-blur-md">
        <div
          ref={listRef}
          role="tablist"
          aria-label="Audiencia"
          onKeyDown={onKeyDown}
          className="mx-auto grid h-12 w-full max-w-md grid-cols-2 rounded-full border border-white/10 bg-pine-800 p-1.5"
        >
          {TABS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              data-tab={key}
              id={`tab-${key}`}
              aria-selected={value === key}
              aria-controls={`panel-${key}`}
              tabIndex={value === key ? 0 : -1}
              onClick={() => {
                select(key);
              }}
              className="relative rounded-full text-sm font-medium text-cream/85 transition-colors hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 aria-selected:text-ink"
            >
              {value === key && (
                <motion.span
                  layoutId="tab-pill"
                  aria-hidden
                  className="absolute inset-0 rounded-full bg-amber shadow-[0_2px_14px_-2px_rgba(237,171,67,0.6)]"
                  transition={reduced ? {duration: 0} : {type: 'spring', stiffness: 400, damping: 34}}
                />
              )}
              <span className="relative z-10">{label}</span>
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={value}
          role="tabpanel"
          id={`panel-${value}`}
          aria-labelledby={`tab-${value}`}
          initial={reduced ? false : {opacity: 0, x: 32 * direction}}
          animate={{opacity: 1, x: 0}}
          exit={reduced ? undefined : {opacity: 0, x: -24 * direction}}
          transition={{duration: 0.35, ease: [0.16, 1, 0.3, 1]}}
        >
          {value === 'vendors' ? <VendorsPanel /> : <PublicPanel />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
